import axios from 'axios';

// Client-side image compression: PNG -> WebP (keeps transparency), large JPEGs recompressed.
// Memory-safe: object URLs revoked and canvases freed after each file.
export const compressImage = (file, maxWidth = 3000) => {
  return new Promise((resolve) => {
    const isPNG = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');

    if (!isPNG && file.size < 500 * 1024) {
      resolve(file);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    const cleanup = (canvas) => {
      URL.revokeObjectURL(objectUrl);
      img.src = '';
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
    };

    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth || height > maxWidth) {
        if (width > height) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        } else {
          width = Math.round((width * maxWidth) / height);
          height = maxWidth;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      const outputType = isPNG ? 'image/webp' : 'image/jpeg';
      const quality = isPNG ? 0.90 : 0.85;
      const newFilename = isPNG
        ? file.name.replace(/\.png$/i, '.webp')
        : file.name;

      canvas.toBlob(
        (blob) => {
          cleanup(canvas);
          if (blob) {
            resolve(new File([blob], newFilename, { type: outputType, lastModified: Date.now() }));
          } else {
            resolve(file);
          }
        },
        outputType,
        quality
      );
    };
    img.onerror = () => {
      cleanup(null);
      resolve(file);
    };
    img.src = objectUrl;
  });
};

// Generic retry wrapper for transient errors (429 rate limit, 5xx, network drops)
export const requestWithRetry = async (fn, maxRetries = 4) => {
  let attempt = 0;
  while (true) {
    try {
      return await fn();
    } catch (error) {
      const status = error.response?.status;
      const retriable = status === 429 || (status >= 500 && status <= 599) || !error.response;
      attempt++;
      if (!retriable || attempt > maxRetries) throw error;
      const waitMs = status === 429 ? 5000 * attempt : 2000 * attempt;
      await new Promise((r) => setTimeout(r, waitMs));
    }
  }
};

// POST with automatic retry on transient errors (429 rate limit, 5xx/520, network drops)
export const postWithRetry = async (url, formData, maxRetries = 5) => {
  let attempt = 0;
  while (true) {
    try {
      return await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000
      });
    } catch (error) {
      const status = error.response?.status;
      const retriable = status === 429 || (status >= 500 && status <= 599) || !error.response;
      attempt++;
      if (!retriable || attempt > maxRetries) throw error;
      const waitMs = status === 429 ? 6000 * attempt : 2500 * attempt;
      await new Promise((r) => setTimeout(r, waitMs));
    }
  }
};
